import type { SparkPath, VfxDefinition, VfxLayer } from "../definition.ts";

/**
 * The `drinkBurst` vfx (P2.4-T41 defines it as the proof of reuse, the plan's option A; P2.8-T89 calls it from a potion
 * drunk, with a tint per potion; ADR-134 decision 7): 800 ms, quiet. A ring widens on the feet and darkens, the aura
 * washes the body for 400 ms, and four sparkles rise from the body one after another. No pillar, no sign, no edge glow,
 * no sound of its own (the potion has its own, ADR-085). Others see it. Under reduced motion only the 300 ms wash stays.
 */

const RISERS: readonly (readonly [number, number])[] = [
  [-9, 0],
  [8, 100],
  [-4, 200],
  [5, 300],
];

/**
 * The four sparkles' flights: each stands at its column, starts 100 ms after the one before, and rises 34 art pixels
 * from just under the middle of the body over 500 ms.
 *
 * @returns The paths.
 */
function risePaths(): SparkPath[] {
  return RISERS.map(([column, delayMs], index) => ({ delayMs, lifeMs: 500, x0: column, y0: 6, x1: column, y1: 6 - 34, light: index % 2 === 0 ? 0 : 1 }));
}

/**
 * The `drinkBurst` definition.
 */
export const DRINK_BURST: VfxDefinition = Object.freeze({
  id: "drinkBurst",
  lifeMs: 800,
  target: "character",
  others: true,
  sound: Object.freeze({ own: null, others: null }),
  layers: Object.freeze<VfxLayer[]>([
    { kind: "ring", fromMs: 0, toMs: 300, radiusFrom: 4, radiusTo: 15, bright: true },
    { kind: "ring", fromMs: 300, toMs: 400, radiusFrom: 15, radiusTo: 18, bright: false },
    {
      kind: "aura",
      keys: [
        [0, 0.2],
        [150, 0.5],
        [400, 0],
      ],
      peak: 0.5,
    },
    { kind: "sparks", fromMs: 0, spanMs: 800, armsUntil: [0.6, 1.1], paths: risePaths() },
  ]),
  reduced: Object.freeze({ lifeMs: 300, flashMs: 300, pillarHalf: 0, auraStep: 2, edgeStep: 0, pulseStep: 0, textStill: false }),
});
